@if (settings.showUniquesTierListMessage()) {
  <div class="info box reset-font" style="margin-bottom: 12px;">
    <div class="info-box-body">
      <div class="close-info-btn" (click)="hideInfoMessage()">✕</div>
      <h3 class="info-title">Explainer: <strong>Unique Tier List</strong></h3>
      <app-content cid="explainer-unique-tier-list" contentType="tip" [conditions]="{ poe1: poe1, poe2: poe2 }"></app-content>
      <p><a class="remove-warning-link" (click)="hideInfoMessage()">(I understand, dismiss)</a></p>
    </div>
  </div>
}

<div class="tier-list-info">
  <div class="tier-input">
    <span>
      <span>Category</span>
      <app-select class="inline" style="width: 140px" [disabled]="false" [(ngModel)]="selectedUniqueCategory" (selectedValueChange)="buildTierList()">
        <app-option [value]="undefined" [text]="'All'"></app-option>
        @for (category of uniqueCategories; track category) {
          <app-option [value]="category" [text]="category"></app-option>
        }
      </app-select>
    </span>

    <input appGlobalShortcutFocus="f" placeholder="Search by name, base type, boss or content" type="text" class="search-filter form-control"
      [(ngModel)]="uniqueFilter" (ngModelChange)="buildTierList()">

    <span class="regular-checkbox show-drop-info-toggle">
      <span class="input-group-btn">
        <button class="btn toggle-btn" [class.off]="!settings.showAllOtherUniques()" (click)="toggleShowAllOther()"></button>
      </span>
      Show all 'Other' tier uniques
    </span>

    <span class="regular-checkbox show-drop-info-toggle">
      <span class="input-group-btn">
        <button class="btn toggle-btn" [class.off]="!settings.alwaysGroupByBaseType()" (click)="toggleAlwaysGroupByBaseType()"></button>
      </span>
      Always group by Base Type
    </span>
  </div>

  <div class="tier-input tier-options">
    <div class="tier-values-toggle-buttons">
      <button type="button" class="btn toggle-button" [class.active]="filter.uniquesTieringMode === 'Normal'" (click)="selectTieringMode('Normal')">Normal</button>
      <button type="button" class="btn toggle-button" [class.active]="filter.uniquesTieringMode === 'Strict'" (click)="selectTieringMode('Strict')">Strict</button>
      <button type="button" class="btn toggle-button" [class.active]="filter.uniquesTieringMode === 'VeryStrict'" (click)="selectTieringMode('VeryStrict')">Very Strict</button>
      <button type="button" class="btn toggle-button toggle-buttom-large-margin" [class.active]="filter.uniquesTieringMode === 'Custom'" (click)="selectTieringMode('Custom')">Custom</button>
      <button type="button" class="btn toggle-button toggle-buttom-equalizer" (click)="openCustomTieringDialog()">
        <span class="icon icon-equalizer"></span>
      </button>
    </div>
  </div>
</div>

<div class="tier-list">
  @for (tierColumn of tierList; track tierColumn.tiers) {
    <div class="tier-column">
      @for (tier of tierColumn.tiers; track tier.tier) {
        <div class="tier"
              (dragenter)="onDragEnter($event, tier)"
              (dragover)="onDragOver($event)"
              (dragleave)="onDragLeave($event, tier)"
              (drop)="onDrop($event, tier)"
              [class.drag-over]="tier.isDragOver">
          <app-tier-style [displayable]="tier.cosmetics" [allowedDisplayTypes]="[DisplayType.Custom]" [label]="tier.name"
            [isHeader]="true" [isTitle]="true" [rarity]="Rarity.Unique" [exampleName]="tier.exampleName" [allowHide]="false"
            [forceHidden]="!tier.cosmetics.displayOptions.active && filter.hideAllRemainingUniques"
            [styleDialogTitle]="tier.styleDialogTitle" (styleUpdated)="disableHideAllRemainingWhenAllUniquesAreEnabled()"></app-tier-style>
          @if (filter.ssf !== true) {
            @if (tier.tier === 'Potential') {
              <app-tooltip-wrapper [right]="true" style="display: inline-block;">
                <span class="tier-subtitle">High Variance <span class="extra-info cursor-default">(?)</span></span>
                <ng-template #tooltip>
                  A Base Type's Tier is determined by the tier of its most valuable Unique. If a Base Type additionally has other, less valuable Uniques without Drop Restrictions, the Base Type might be moved to this 'Potential' tier:
                  <br />- For <strong>Excellent</strong> Uniques: either if the lowest value is less than {{ downgradeExcellentByLowestValueThreshold }}{{ currency }} or the average value is less than {{ downgradeExcellentByAverageValueThreshold }}{{ currency }}.
                  <br />- For <strong>Good</strong> Uniques: either if the lowest value is less than {{ downgradeGoodThreshold }}{{ currency }} or the average value is less than {{ downgradeGoodThreshold }}{{ currency }}.
                </ng-template>
              </app-tooltip-wrapper>
            } @else if (tier.tier === 'Restricted') {
              <app-tooltip-wrapper [right]="true" style="display: inline-block;">
                <span class="tier-subtitle">Drop-Restricted <span class="extra-info cursor-default">(?)</span></span>
                <ng-template #tooltip>
                  Base Types for which the valuable Uniques can only drop from Bosses. If you don't do bosses, you may want to treat these as 'Other' uniques,
                  because these uniques will never be valuable while mapping.
                </ng-template>
              </app-tooltip-wrapper>
            } @else {
              <div class="tier-subtitle">
                {{ tier.specialLabel ? tier.specialLabel : tier.priceLabel }} @if (!tier.specialLabel) { <img [src]="tier.priceBaseCurrencyImage ? imgPrefix + tier.priceBaseCurrencyImage : ''" [alt]="tier.priceBaseCurrency" class="base-type-image" width="18" height="18" loading="lazy"> }
              </div>
            }
          }
          @if (tier.tier === 'Class') {
            <div class="unique-filter">
              <app-autocomplete
                [allLabel]="'None'"
                [options]="classes"
                [disabled]="false"
                [showOptions]="true"
                [selectedValues]="filter.showClassSpecificUniquesForClasses"
                (selectedValuesChange)="filter.showClassSpecificUniquesForClasses = $event; rebuildTierList();"></app-autocomplete>
            </div>
          }
          @if (tier.tier === 'Other' && (!uniqueFilter || uniqueFilter.length < 2) && !settings.showAllOtherUniques() && !changedOnly) {
            <div class="tier-item-types">
              <div class="tier-item-type">
                <div class="tier-item-type-title"><h4>Search for Other Uniques</h4></div>
                <div class="extra-info tier-placeholder-text">
                  Start typing in the search field above
                  <br />to search through all other uniques.
                </div>
              </div>
            </div>
          }
          <div class="tier-item-types">
            @for (classGroup of (tier.tier === 'Other' ? filteredOtherBaseTypes : tier.classes); track classGroup.class) {
            <div class="tier-item-type">
              @if (classGroup.class !== 'Default' && (tier.tier !== 'Other' || (uniqueFilter && uniqueFilter.length > 1) || settings.showAllOtherUniques())) {
                <div class="tier-item-type-title"><h4>{{ classGroup.class }}</h4></div>
              }
              <div class="tier-item-type-body">
                  @for (baseType of classGroup.items; track baseType.baseType) {
                    <div class="tier-item-type-base-type"
                      draggable="true"
                      (dragstart)="onDragStart($event, baseType)"
                      (dragend)="onDragEnd()"
                      [class.dragged]="draggedItem?.baseType === baseType.baseType"
                      [class.tier-item-inactive]="baseType.inactiveByFilter">
                        <div>
                          @if (settings.alwaysGroupByBaseType() || baseType.allUniques.length > 1) {
                            <app-tooltip-wrapper style="display: inline-block;">
                              <span class="normal-item-color">{{ baseType.baseType }}</span>
                              @if (baseType.nonRestrictedUniques.length > 1) {
                                <ng-template #tooltip>
                                  <span>
                                    <strong>Non-Restricted Uniques</strong>
                                    <br /><span class="normal-item-color">Average:</span> {{ baseType.averageValue }}{{ currency }}<span class="normal-item-color">, Lowest:</span> {{ baseType.lowestValue }}{{ currency }}
                                  </span>
                                  <span class="tooltip-section">
                                    @for (unique of baseType.nonRestrictedUniques; track unique.name; let i = $index) {
                                      @if (i > 0) { <br /> }
                                      <span class="normal-item-color">{{ unique.name }}:</span> {{ unique.price }}{{ currency }}
                                    }
                                  </span>
                                </ng-template>
                              }
                            </app-tooltip-wrapper>
                          } @else {
                            <app-item-label
                              [unique]="true"
                              [baseType]="baseType.allUniques[0].name"
                              [image]="baseType.allUniques[0].image"
                              [hideTooltip]="draggedItem?.baseType == baseType.baseType"
                              [manualTier]="baseType.manualTier"
                            ></app-item-label>
                          }

                        @if (poe1 && baseType.averageNonRestrictedDustValue) {
                          <img class="inline-image meta-info-icon" src="{{ imgPrefix }}/img/icons/dust.png" title="Average Size-Corrected Non-Restricted Dust value: {{ baseType.averageNonRestrictedDustValue }}. Minimum: {{ baseType.minNonRestrictedDustValue }}" width="18" height="18" loading="lazy" />
                        }

                        @if (baseType.pinnedFavouriteContentIcon) {
                          <app-tooltip-wrapper [right]="true" style="display: inline-block;">
                            <img class="inline-image meta-info-icon" [src]="imgPrefix + baseType.pinnedFavouriteContentIcon" alt="" width="18" height="18" loading="lazy" />
                            <ng-template #tooltip>
                              Favourite: still shown because you selected {{ baseType.pinnedFavouriteContentLabel }}
                            </ng-template>
                          </app-tooltip-wrapper>
                        }

                        @if (baseType.pinnedOtherLowValueBossShown) {
                          <app-tooltip-wrapper [right]="true" style="display: inline-block;">
                            <span class="icon icon-lock meta-info-icon"></span>
                            <ng-template #tooltip>
                              Still shown because you you have 'Show Other Low-value Boss-only uniques' enabled.
                            </ng-template>
                          </app-tooltip-wrapper>
                        }

                        @if (baseType.originalTier && baseType.originalTier !== tier.tier) {
                          <span class="original-tier" (click)="resetToOriginalTier(baseType)"
                            title="Reset to original {{ baseType.originalTier }}-tier">↻</span>
                        } @else {
                          @if (baseType.movedUp || baseType.movedDown) {
                            <app-tooltip-wrapper [right]="true" style="display: inline-block;">
                              @for (i of baseType.movedUpArray; track i) {
                                <span class="moved up">↑</span>
                              }
                              @for (i of baseType.movedDownArray; track i) {
                                <span class="moved down">↓</span>
                              }
                              <ng-template #tooltip>
                                <strong>{{ baseType.baseType }}</strong>
                                <br /><span class="normal-item-color">Previous tier:</span> {{ baseType.previousTier }}
                                <br /><span class="normal-item-color">Current tier:</span> {{ tier.tier }}
                                @for (i of baseType.movedUpArray; track i) {
                                  <span class="moved up">↑</span>
                                }
                                @for (i of baseType.movedDownArray; track i) {
                                  <span class="moved down">↓</span>
                                }
                              </ng-template>
                            </app-tooltip-wrapper>
                          }

                          @if (!settings.alwaysGroupByBaseType() && baseType.allUniques.length === 1) {
                            <app-price-type-indicator [priceType]="baseType.allUniques[0].type" [isUnique]="true"></app-price-type-indicator>
                          }
                        }
                      </div>

                      @if (settings.alwaysGroupByBaseType() || baseType.allUniques.length > 1) {
                        @for (unique of baseType.allUniques; track unique.name) {
                          <div>
                            <app-item-label
                              [unique]="true"
                              [baseType]="unique.name"
                              [image]="unique.image"
                              [hideTooltip]="draggedItem?.baseType == baseType.baseType"
                              [class.cheap-unique]="unique.price && unique.price < goodThreshold"
                              [manualTier]="baseType.manualTier"
                            ></app-item-label>

                            <app-price-type-indicator [priceType]="unique.type" [isUnique]="true"></app-price-type-indicator>
                          </div>
                        }
                      }
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  }
</div>

@if (showCustomTieringDialog) {
  <app-custom-uniques-tiering-mode-dialog
    [filter]="filter"
    (close)="closeCustomTieringDialog()">
  </app-custom-uniques-tiering-mode-dialog>
}
